import React from "react";
import { Link } from "react-router-dom";
import { Table, Tooltip, Tag, Dropdown, MenuProps } from "antd";
import type { ColumnsType } from "antd/es/table";
import type { Job, JobFilters } from "../../../../core/data/redux/recruitment/types";
import { JOB_STATUS_COLORS } from "../../constants/recruitment.constants";
import CustomPagination from "./CustomPagination";
import { humanReadableDate } from "../../../../helper/dateTimeHelper";
import { notify } from "../../../../services/toast.config";
import { all_routes } from "../../../router/all_routes";

interface JobTableProps {
  jobs: Job[];
  loading: boolean;
  filters: JobFilters;
  totalCount: number;
  onFilterChange: (partial: Partial<JobFilters>) => void;
  onEdit: (job: Job) => void;
  onDelete: (jobId: number) => void;
  onPublish: (jobId: number) => void;
  onClone: (job: Job) => void;
  onChangeStatus: (jobId: number, status: string) => void;
  onCloseJob: (job: Job) => void;
  onUnpublish: (jobId: number, platform: string) => void;
  departmentMap: Record<number, string>;
}

const antStatusColors: Record<string, string> = {
  success: "green",
  primary: "blue",
  warning: "orange",
  danger: "red",
  secondary: "default",
  info: "cyan",
};

const JobTable: React.FC<JobTableProps> = ({
  jobs,
  loading,
  filters,
  totalCount,
  onFilterChange,
  onEdit,
  onDelete,
  onPublish,
  onClone,
  onChangeStatus,
  onCloseJob,
  onUnpublish,
  departmentMap
}) => {
  const getActionItems = (job: Job): MenuProps["items"] => {
    const items: MenuProps["items"] = [
      {
        key: "edit",
        icon: <i className="ti ti-edit" style={{ color: "#3b82f6" }} />,
        label: "Edit Job",
        onClick: () => onEdit(job),
      },
      {
        key: "clone",
        icon: <i className="ti ti-copy" style={{ color: "#8b5cf6" }} />,
        label: "Create Similar",
        onClick: () => onClone(job),
      },
      {
        key: "publish",
        icon: <i className="ti ti-share" style={{ color: "#10b981" }} />,
        label: "Publish Options",
        onClick: () => onPublish(job.id),
      },
      {
        key: "copy-url",
        icon: <i className="ti ti-link" style={{ color: "#0ea5e9" }} />,
        label: job.job_share_url ? "Copy Share URL" : "Share URL (page offline)",
        disabled: !job.job_share_url,
        onClick: () => {
          navigator.clipboard.writeText(job.job_share_url!);
          notify.success("Job URL copied to clipboard!");
        },
      },
      { type: "divider" },
      {
        key: "status-group",
        type: "group",
        label: "Change Status",
        children: [
          {
            key: "status-active",
            label: <span className={job.status === "active" ? "fw-bold text-success" : ""}>Active</span>,
            disabled: job.status === "active",
            onClick: () => onChangeStatus(job.id, "active"),
          },
          {
            key: "status-draft",
            label: <span className={job.status === "draft" ? "fw-bold text-warning" : ""}>Draft</span>,
            disabled: job.status === "draft",
            onClick: () => onChangeStatus(job.id, "draft"),
          },
        ]
      },
      { type: "divider" },
      {
        key: "close",
        icon: <i className="ti ti-lock" style={{ color: "#ef4444" }} />,
        label: "Close Job",
        disabled: job.status === "closed",
        onClick: () => onCloseJob(job),
      },
    ];

    
    if (job.platforms && job.platforms.length > 0) {
      const publishedOn = job.platforms.filter((p) => p.status === "published");
      if (publishedOn.length > 0) {
        items.push({ type: "divider" });
        items.push({
          key: "unpublish-group",
          type: "group",
          label: "Unpublish From",
          children: publishedOn.map((p) => ({
            key: `unpublish-${p.platform}`,
            label: <span className="text-danger">{(p.platform === "career_page" ? "Career Page" : p.platform.charAt(0).toUpperCase() + p.platform.slice(1))}</span>,
            onClick: () => onUnpublish(job.id, p.platform),
          }))
        });
      }
    }

    items.push({ type: "divider" });
    items.push({
      key: "delete",
      icon: <i className="ti ti-trash" />,
      danger: true,
      label: "Delete",
      onClick: () => onDelete(job.id),
    });

    return items;
  };

  const columns: ColumnsType<Job> = [
    {
      title: "SrNo.",
      dataIndex: "srNo",
      key: "srNo",
      width: 70,
      align: "center",
      render: (_, __, index) => (
        <span style={{ fontSize: 12, fontWeight: 500, color: "#6b7280" }}>
          {((filters.page || 1) - 1) * (filters.limit || 10) + index + 1}
        </span>
      ),
    },
    {
      title: <Tooltip title="Job Title & Location Details">Job Title</Tooltip>,
      dataIndex: "job_title",
      key: "job_title",
      width: 260,
      sorter: (a, b) => a.job_title.localeCompare(b.job_title),
      render: (text, record) => (
        <Tooltip
          title={
            <div style={{ padding: 4 }}>
              <div className="fw-bold pb-1 mb-1 border-bottom border-secondary">{record.job_title}</div>
              <div style={{ fontSize: 12 }}>
                <i className="ti ti-building me-1" />
                {record.department_id && departmentMap?.[record.department_id] || "—"}  
              </div>
              <div style={{ fontSize: 12 }}><i className="ti ti-map-pin me-1" />{record.work_location}</div>
              {record.min_salary && (
                <div style={{ fontSize: 12 }}>
                  <i className="ti ti-currency-rupee me-1" />
                  {record.min_salary?.toLocaleString("en-IN")} – {record.max_salary?.toLocaleString("en-IN")}
                </div>
              )}
            </div>
          }
          color="#1e293b"
          placement="right"
        >
          <div className="d-flex flex-column">
            <h6 className="fw-medium mb-1 text-truncate" style={{ maxWidth: 240, fontSize: 13.5, color: "#0f172a" }}>
              <Link to={all_routes.jobDetails.replace(":id", String(record.id))} style={{ color: "inherit", textDecoration: "none" }} className="text-primary-hover">
                {text}
              </Link>
            </h6>
            <span className="text-muted text-truncate" style={{ fontSize: 12, maxWidth: 240 }}>
              <i className="ti ti-map-pin me-1" />{record.work_location}
            </span>
          </div>
        </Tooltip>
      ),
    },
    {
      title: <Tooltip title="Job Department">Department</Tooltip>,
      dataIndex: "department_id",
      key: "department",
      width: 140,
      render: (deptId) => {
        const name = departmentMap?.[deptId] || "—";
        return (
          <Tooltip title={name}>
            <span className="text-truncate d-inline-block" style={{ fontSize: 13, maxWidth: 120 }}>
              {name}
            </span>
          </Tooltip>
        );
      },
    },
    {
      title: <Tooltip title="Employment Type">Type</Tooltip>,
      dataIndex: "employment_type",
      key: "employment_type",
      width: 110,
      render: (text) => (
        <Tooltip title={text}>
          <Tag color="cyan" style={{ fontSize: 11.5, margin: 0 }}>{(text || "").replace(/_/g, " ")}</Tag>
        </Tooltip>
      ),
    },
    {
      title: <Tooltip title="Total Number of Applications">Applicants</Tooltip>,
      dataIndex: "application_count",
      key: "application_count",
      width: 100,
      align: "center",
      render: (count) => (
        <Tooltip title={`${count || 0} applications received`}>
          <Tag color="blue" style={{ margin: 0 }}>{count || 0}</Tag>
        </Tooltip>
      ),
    },
    {
      title: <Tooltip title="Creation Date">Posted On</Tooltip>,
      dataIndex: "created_at",
      key: "created_at",
      width: 120,
      sorter: (a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime(),
      render: (date) => (
        <Tooltip title={humanReadableDate(date)}>
          <span className="text-muted" style={{ fontSize: 12.5 }}>
            <i className="ti ti-calendar me-1" />
            {humanReadableDate(date)}
          </span>
        </Tooltip>
      ),
    },
    {
      title: <Tooltip title="Current posting status">Status</Tooltip>,
      dataIndex: "status",
      key: "status",
      width: 90,
      render: (status) => {
        const bsColor = JOB_STATUS_COLORS[status as keyof typeof JOB_STATUS_COLORS] || "secondary";
        return (
          <Tooltip title={`Status is ${status}`}>
            <Tag color={antStatusColors[bsColor] || "default"} style={{ fontSize: 11.5, textTransform: "capitalize", margin: 0 }}>
              {status}
            </Tag>
          </Tooltip>
        );
      },
    },
    {
      title: <Tooltip title="Published Platforms">Published On</Tooltip>,
      dataIndex: "platforms",
      key: "platforms",
      width: 160,
      render: (platforms: Job["platforms"]) => {
        if (!platforms || platforms.length === 0) {
          return <span className="text-muted" style={{ fontSize: 12 }}>—</span>;
        }

        const published = platforms;
        if (published.length === 0) {
          return <span className="text-muted" style={{ fontSize: 12 }}>—</span>;
        }

        const MAX_VISIBLE = 2;
        const visible = published.slice(0, MAX_VISIBLE);
        const overflow = published.slice(MAX_VISIBLE);

        const getPlatformLabel = (platform: string) =>
          platform === "career_page"
            ? "Career"
            : platform.charAt(0).toUpperCase() + platform.slice(1);

        const getPlatformColor = (status: string) =>
          status === "published" ? "green" : status === "pending" ? "orange" : "red";

        return (
          <div className="d-flex flex-wrap align-items-center gap-1">
            {visible.map((p) => (
              <Tooltip
                key={p.platform}
                title={`${p.platform === "career_page" ? "Career Page" : p.platform.charAt(0).toUpperCase() + p.platform.slice(1)} · ${p.status}`}
              >
                <Tag
                  color={getPlatformColor(p.status)}
                  style={{ fontSize: 10.5, margin: 0, lineHeight: "18px", cursor: "help" }}
                >
                  {getPlatformLabel(p.platform)}
                </Tag>
              </Tooltip>
            ))}

            
            {overflow.length > 0 && (
              <Tooltip
                title={
                  <div className="d-flex flex-column gap-1" style={{ padding: "2px 0" }}>
                    {overflow.map((p) => (
                      <span key={p.platform} style={{ fontSize: 12 }}>
                        <span
                          style={{
                            display: "inline-block",
                            width: 7,
                            height: 7,
                            borderRadius: "50%",
                            background: p.status === "published" ? "#4ade80" : p.status === "pending" ? "#fb923c" : "#f87171",
                            marginRight: 6,
                          }}
                        />
                        {p.platform === "career_page" ? "Career Page" : p.platform.charAt(0).toUpperCase() + p.platform.slice(1)}
                        {" "}· {p.status}
                      </span>
                    ))}
                  </div>
                }
                color="#1e293b"
              >
                <Tag
                  style={{
                    fontSize: 10.5,
                    margin: 0,
                    lineHeight: "18px",
                    cursor: "help",
                    background: "#f1f5f9",
                    color: "#475569",
                    border: "1px solid #cbd5e1",
                  }}
                >
                  +{overflow.length} more
                </Tag>
              </Tooltip>
            )}
          </div>
        );
      },
    },
    {
      title: "Actions",
      key: "action",
      width: 80,
      align: "center",
      render: (_, record) => (
        <Dropdown menu={{ items: getActionItems(record) }} trigger={["click"]} placement="bottomRight">
          <button
            className="btn btn-icon btn-sm btn-light"
            style={{ borderRadius: "6px" }}
            onClick={(e) => e.stopPropagation()}
          >
            <i className="ti ti-dots-vertical" />
          </button>
        </Dropdown>
      ),
    },
  ];

  return (
    <div className="card-body p-0 mb-3 border-0 slide-up-enter">
      {loading ? (
        <div className="d-flex justify-content-center align-items-center bg-white" style={{ minHeight: 400, borderRadius: 12 }}>
          <div className="text-center">
            <div className="spinner-border text-primary" role="status" style={{ width: "3rem", height: "3rem" }} />
            <p className="mt-3 text-muted">Loading jobs...</p>
          </div>
        </div>
      ) : jobs.length > 0 ? (
        <div className="bg-white" style={{ borderRadius: 12, overflow: "hidden", border: "1px solid #e2e8f0" }}>
          <Table
            className="custom-data-table"
            columns={columns}
            dataSource={jobs}
            rowKey="id"
            size="middle"
            pagination={false}
            scroll={{ x: 1220 }}
            rowClassName={(record, index) =>
              index % 2 === 0 ? "data-table-row-light" : "data-table-row-dark"
            }
          />
          <CustomPagination
            currentPage={filters.page || 1}
            totalPages={Math.ceil(totalCount / (filters.limit || 10))}
            total={totalCount}
            limit={filters.limit || 10}
            hasNextPage={(filters.page || 1) * (filters.limit || 10) < totalCount}
            hasPrevPage={(filters.page || 1) > 1}
            onPageChange={(page) => onFilterChange({ page })}
            onLimitChange={(limit) => onFilterChange({ limit, page: 1 })}
          />
        </div>
      ) : (
        <div className="d-flex justify-content-center align-items-center bg-white" style={{ minHeight: 400, borderRadius: 12 }}>
          <div className="text-center">
            <i className="ti ti-file-search text-muted" style={{ fontSize: "4rem" }} />
            <h5 className="mt-3 mb-2 fw-semibold">No Jobs Found</h5>
            <p className="text-muted mb-0">Try adjusting your filters or create a new job posting</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default JobTable;
